<script setup lang="ts">
import { onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useStore } from "vuex"

import { AxiosError } from "axios"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import LayoutNotAuthed from "@/components/shared/LayoutNotAuthed.vue"
import partnerService from "@/services/partner"
import { apiGuideMessage, ErrorCode } from "@/utils/error"

const store = useStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const partnerId = ref("")
const password = ref("")

const isSubmitting = ref(false)
const errorMessage = ref("")

async function logIn() {
  isSubmitting.value = true
  errorMessage.value = ""

  try {
    const data = await partnerService.signIn({ partnerId: partnerId.value, password: password.value })

    store.commit("saveToken", data.token)
    // await 없이 넘어가면 partner 가 store 에 들어오기 전에 다음 화면이 mount 된다.
    // redirect 가 /orders/:id 인 경우 Order.vue 의 loadOrder 가 partner.supplierType 을
    // 읽다 크래시하고 /orders 로 튕겨나갔다(PARTNER-FRONTEND-XV).
    await store.dispatch("loadPartner", { force: true })

    const path = (route.query.redirect || "/") as string
    router.replace(path) // replace history so user can’t hit “back” into login
  } catch (error) {
    isSubmitting.value = false
    if (error instanceof AxiosError && error.response) {
      const { code } = error.response.data
      switch (code) {
        case ErrorCode.INVALID_ID:
          errorMessage.value = t("log_in.errors.invalid_id")
          break
        case ErrorCode.INVALID_PASSWORD:
          errorMessage.value = t("log_in.errors.invalid_password")
          break
        default:
          // 응답의 message 는 에러 클래스명("NotAcceptable")이라 사용자에게 뜻이 없다.
          errorMessage.value = apiGuideMessage(error.response.data) || t("shared.errors.unknown")
      }
    }
  }
}

onMounted(() => {
  const partner = store.getters.partner
  if (partner) {
    router.replace("/")
  }
})
</script>

<template>
  <LayoutNotAuthed>
    <div class="row justify-content-center" id="main-container">
      <div class="col-12 col-lg-6 col-xxl-4">
        <Card>
          <CardTitle>{{ $t("log_in.title") }}</CardTitle>
          <form @submit.prevent="logIn">
            <CardContent>
              <InputBox
                v-model="partnerId"
                type="text"
                :label="$t('log_in.id')"
                field="partnerId"
                required
                suppressRequiredMark
              />
              <InputBox
                v-model="password"
                type="password"
                :label="$t('log_in.password')"
                field="password"
                required
                suppressRequiredMark
              />
            </CardContent>
            <CardActions displayBlock>
              <Button type="submit" :text="$t('log_in.action')" :isSubmitting="isSubmitting" />
            </CardActions>
            <div v-if="errorMessage" class="mt-3 text-danger text-center">{{ errorMessage }}</div>
          </form>
        </Card>
      </div>
    </div>
  </LayoutNotAuthed>
</template>

<style scoped>
#main-container {
  margin-top: 5rem;
  margin-bottom: 5rem;
}

@media (max-width: 991.98px) {
  #main-container {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
}
</style>
